import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type AuthCatalog } from "@/lib/api";
import { ErrorBox, Spinner } from "@/components/ui";

export function AuthSchemaPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [catalog, setCatalog] = useState<AuthCatalog | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.authCatalog(id);
        setCatalog(data);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading auth schema…" />;

  return (
    <div className="space-y-5">
      {error ? <ErrorBox error={error} /> : null}
      {catalog ? (
        <>
          <p className="text-sm text-text-muted">{catalog.description}</p>
          <p className="text-xs text-text-muted">
            Product schema:{" "}
            <code className="font-mono text-text-primary">{catalog.schema}</code>. Project
            SQL can read <code className="font-mono text-text-primary">auth.users</code>
            and call <code className="font-mono text-text-primary">auth.uid()</code> /{" "}
            <code className="font-mono text-text-primary">auth.role()</code>. Session rows
            remain owner-API only.
          </p>
          {catalog.relations.map((rel) => (
            <div key={rel.name} className="card p-4">
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-sans text-sm font-semibold text-text-primary">
                  {catalog.schema}.{rel.name}
                </h3>
                <span className="font-mono text-[11px] text-text-muted">
                  source: {rel.source}
                </span>
              </div>
              <p className="mb-3 text-sm text-text-muted">{rel.description}</p>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Type</th>
                      <th>Safe surface</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rel.columns.map((c) => (
                      <tr key={c.name}>
                        <td className="font-mono text-xs">
                          {c.name}
                          {c.sensitive ? (
                            <span className="ml-1 mono-chip text-[10px]">
                              sensitive
                            </span>
                          ) : null}
                        </td>
                        <td className="font-mono text-xs text-text-muted">
                          {c.type}
                          {c.nullable ? "?" : ""}
                        </td>
                        <td>
                          {c.exposed ? (
                            <span className="mono-chip border-success bg-success-container text-success">
                              exposed
                            </span>
                          ) : (
                            <span className="mono-chip">hidden</span>
                          )}
                        </td>
                        <td className="text-xs text-text-muted">{c.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}
